import { z } from "zod";
import { EMPRESAS } from "./fase.js";

// =====================================================================
// Catálogo de entidades (PORTAL_DESIGN §7). Descreve cada tabela editável:
// a SPA renderiza o formulário a partir daqui e a API valida com o MESMO
// schema Zod gerado deste catálogo. Fonte de verdade única (sem form hard-coded).
// =====================================================================

export const UF_LIST = [
  "GO", "SC", "SP", "PR", "BA", "MG", "PE", "RS", "MS", "MT", "RJ", "TO", "ES", "PA", "CE",
] as const;

// Status de ENTRADA da carga (dropdown da planilha).
export const STATUS_ENTRADA = [
  "N ACEITA", "ACEITA", "VISTORIA", "CHEK OK", "WHATS", "BALANÇA", "MANUT",
] as const;

// Status de META (AF) usado nos Resumos.
export const STATUS_META = ["LIBERADO", "AGENDADO", "CARREGADO"] as const;

// "BOBINÃO" é um valor real da operação (55 linhas no histórico) que ficava
// fora da lista e travava a edição da viagem. Só "FRETE ESPECIAL" aplica os
// +30% (ver calc.tarifa); os demais são rótulos de tipo de carga.
export const FRETE_ESPECIAL_OPTIONS = ["", "FRETE ESPECIAL", "BOBINÃO"] as const;
export const VINCULO_VEICULO_OPTIONS = ["Frota", "Agregado"] as const;
export const CARROCERIA_OPTIONS = [
  "Grade baixa", "Sider", "Total sider", "Capota", "Graneleiro", "Outra",
] as const;

// Materiais de amarração/segurança que o motorista carrega (checklist do perfil).
// Cintas e catracas pedidos na revisão; lona/cantoneira somam ao controle.
export const MATERIAIS_MOTORISTA = ["Cinta", "Catraca", "Lona", "Cantoneira"] as const;

export type FieldType =
  | "text"
  | "number"
  | "currency"
  | "date"
  | "datetime"
  | "enum"
  | "reference"
  | "plate"
  | "cpf"
  | "boolean"
  | "textarea"
  | "checklist"
  | "json";

export interface FieldSpec {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  unique?: boolean;
  hint?: string;
  options?: readonly string[]; // enum
  items?: readonly string[]; // checklist — itens conferíveis (ex.: materiais)
  ref?: { entity: string; valueField: string; labelField: string }; // reference
  computed?: boolean; // derivado pelo calc engine — read-only no form, ignorado no input
  pii?: boolean; // mascarado para papéis não autorizados
}

export interface EntitySpec {
  entity: string; // slug único (ex.: "frota")
  table: string; // schema.tabela (ex.: "dim.frota")
  label: string;
  primaryKey: string;
  group: "cadastro" | "operacao" | "programacao";
  showInCadastros?: boolean; // false mantém a entidade/API, mas retira a aba da tela de Cadastros
  fields: FieldSpec[];
}

const f = (name: string, label: string, type: FieldType, extra: Partial<FieldSpec> = {}): FieldSpec => ({
  name, label, type, ...extra,
});

// ---------------------------------------------------------------------
// Definições das entidades (colunas confirmadas em DATA_MODEL §4 e migrations).
// ---------------------------------------------------------------------
export const ENTITIES: EntitySpec[] = [
  {
    entity: "frota", table: "dim.frota", label: "Frota", primaryKey: "id", group: "cadastro", showInCadastros: false,
    fields: [
      f("motorista", "Motorista", "text", { required: true, unique: true, hint: "Chave usada nas operações (VLOOKUP)" }),
      f("cavalo", "Cavalo (placa)", "plate", { required: true }),
      f("primeira_carreta", "1ª carreta", "plate"),
      f("dolly", "Dolly", "plate"),
      f("segunda_carreta", "2ª carreta", "plate"),
      f("status", "Status", "text"),
      f("materiais", "Materiais (checklist)", "checklist", {
        items: MATERIAIS_MOTORISTA,
        hint: "Confira cintas, catracas e demais itens de amarração do motorista",
      }),
    ],
  },
  {
    entity: "clientes", table: "dim.clientes", label: "Clientes", primaryKey: "id", group: "cadastro", showInCadastros: false,
    fields: [
      f("cliente", "Cliente", "text", { required: true }),
      f("cidade", "Cidade", "text"),
      f("horarios", "Horários", "text"),
      f("obs", "Observação", "textarea"),
      f("contato", "Contato", "text"),
      f("email", "E-mail", "text"),
      f("endereco", "Endereço", "textarea"),
    ],
  },
  {
    entity: "tarifas", table: "dim.tarifas", label: "Tarifas", primaryKey: "id", group: "cadastro",
    fields: [
      f("uf_destino", "UF destino", "enum", { options: UF_LIST }),
      f("cidade", "Cidade", "text", { required: true }),
      f("tarifa_vega", "Tarifa VEGA", "currency"),
      f("frete_especial", "Frete especial", "currency"),
      f("tarifa_cl", "Tarifa CL", "currency"),
    ],
  },
  {
    entity: "regioes", table: "dim.regioes", label: "Regiões", primaryKey: "id", group: "cadastro",
    fields: [
      f("cidade_polo", "Cidade polo", "text", { required: true }),
      f("uf", "UF", "enum", { options: UF_LIST }),
      f("regiao", "Região da meta", "text", { hint: "ex.: 02_REGIÃO_CURITIBA" }),
    ],
  },
  {
    entity: "regioes_usiminas", table: "dim.regioes_usiminas", label: "Regiões (Usiminas)", primaryKey: "id", group: "cadastro",
    fields: [
      f("cidade_polo", "Cidade polo", "text", { required: true }),
      f("uf", "UF", "enum", { options: UF_LIST }),
    ],
  },
  {
    entity: "pbtc", table: "dim.pbtc", label: "Veículos", primaryKey: "id", group: "cadastro",
    fields: [
      f("placa", "Placa", "plate", { required: true }),
      f("vinculo", "Vínculo", "enum", { options: VINCULO_VEICULO_OPTIONS }),
      f("renavam", "Renavam", "text", { unique: true }),
      f("chassi", "Chassi", "text", { unique: true }),
      f("carroceria", "Carroceria", "enum", { options: CARROCERIA_OPTIONS }),
      f("marca", "Marca", "text"),
      f("modelo", "Modelo", "text"),
      f("cor", "Cor", "text"),
      f("ano_fabricacao", "Ano de fabricação", "number"),
      f("ano_modelo", "Ano do modelo", "number"),
      f("quantidade_eixos", "Quantidade de eixos", "number"),
      f("uf", "UF", "enum", { options: UF_LIST }),
      f("cidade", "Cidade", "text"),
      f("crv", "CRV", "text"),
      f("proprietario", "Proprietário", "text"),
      f("documento_proprietario", "CPF/CNPJ do proprietário", "text", { pii: true }),
      f("cidade_proprietario", "Cidade do proprietário", "text"),
      f("data_documento", "Data do documento", "date"),
      f("meta_mensal_toneladas", "Meta mensal (t)", "number"),
      f("numero_rastreador", "Número do rastreador", "text"),
      f("documento_veiculo_url", "Documento do veículo (URL)", "text", {
        hint: "Link autorizado; o arquivo não é armazenado no banco",
      }),
      f("documento_veiculo_metadata", "Metadados do documento", "json"),
      f("peso_tara_max", "Tara máx (kg)", "number"),
      f("peso_tara_min", "Tara mín (kg)", "number"),
      f("peso_tara_media", "Tara média (kg)", "number"),
      f("tipo", "Tipo", "text", { hint: "ex.: UK, U5, UI, UB" }),
      f("limite", "Limite PBTC (kg)", "number"),
    ],
  },
  {
    entity: "curso_autor", table: "dim.curso_autor", label: "Motoristas", primaryKey: "id", group: "cadastro",
    fields: [
      f("cpf", "CPF", "cpf", { pii: true }),
      f("nome", "Nome", "text", { required: true }),
      f("contato", "Contato", "text", { pii: true }),
      f("validade", "Validade do curso Autor", "date"),
      f("curso", "Curso", "text"),
      f("validade_exame", "Validade do exame periódico", "date"),
      f("tipo_exame", "Tipo de exame", "text"),
      f("endereco", "Endereço", "textarea", { pii: true }),
      f("chave_pix", "Chave Pix", "text", { pii: true }),
      f("cnh_numero", "Número da CNH", "text", { pii: true }),
      f("cnh_categoria", "Categoria da CNH", "text"),
      f("cnh_validade", "Validade da CNH", "date"),
      f("cnh_anexo_url", "CNH (URL)", "text", {
        pii: true,
        hint: "Link autorizado; o arquivo não é armazenado no banco",
      }),
      f("placas_vinculadas", "Placas vinculadas", "text", {
        hint: "Informe uma ou mais placas separadas por vírgula",
      }),
      f("observacao", "Observação", "textarea"),
      f("dias_faltantes", "Dias faltantes", "number", { computed: true }),
    ],
  },
  {
    entity: "exame_periodico", table: "dim.exame_periodico", label: "Exame periódico", primaryKey: "id", group: "cadastro", showInCadastros: false,
    fields: [
      f("cpf", "CPF", "cpf", { pii: true }),
      f("nome", "Nome", "text", { required: true }),
      f("contato", "Contato", "text", { pii: true }),
      f("validade", "Validade", "date"),
      f("exame", "Exame", "text"),
      f("dias_faltantes", "Dias faltantes", "number", { computed: true }),
    ],
  },
  {
    entity: "data_programacao", table: "dim.data_programacao", label: "Programação (DATA)", primaryKey: "id", group: "programacao",
    fields: [
      f("transporte", "Nº transporte", "text", { required: true, hint: "Chave da ordem (junta com a operação)" }),
      f("codigo_bntg", "Código transportador", "text"),
      f("data_programacao", "Data", "date"),
      f("hora_programacao", "Hora", "text"),
      f("veiculo", "Tipo de veículo", "text"),
      f("nome_cliente", "Cliente", "text"),
      f("cidade_cliente", "Cidade", "text"),
      f("peso", "Peso (t)", "number"),
      f("lote", "Lote", "text"),
      f("patio", "Pátio", "text"),
      f("prioridade", "Prioridade", "text"),
    ],
  },
  {
    // A tabela nasceu VEGA-only (migration 007) e hoje guarda todas as
    // empresas (018). O slug segue `viagem_vega` porque é contrato público:
    // rota /entities/viagem_vega, matriz de RBAC e o `entity` gravado em cada
    // linha da auditoria. Ver docs/DECISIONS.md D-21.
    entity: "viagem_vega", table: "ops.viagem_vega", label: "Operação (viagens)", primaryKey: "id", group: "operacao",
    fields: [
      // NÃO obrigatório de propósito: a coluna tem DEFAULT 'VEGA' no banco e
      // exigi-la aqui quebraria todo POST /entities/viagem_vega já existente.
      f("empresa", "Empresa", "enum", { options: EMPRESAS, hint: "Quem contrata o frete — agrupamento da Torre de Controle. Em branco = VEGA." }),
      f("competencia", "Competência", "date", { hint: "1º dia do mês de referência" }),
      f("numero_ordem", "Nº da ordem", "text", { required: true, hint: "Puxa dados de DATA (programação)" }),
      f("motorista", "Motorista", "reference", { ref: { entity: "frota", valueField: "motorista", labelField: "motorista" }, hint: "Busque o motorista ou deixe em branco para vincular depois" }),
      f("cavalo", "Cavalo", "plate", { ref: { entity: "pbtc", valueField: "placa", labelField: "placa" }, hint: "Placa do cavalo nesta carga" }),
      f("primeira_carreta", "1ª carreta", "plate", { ref: { entity: "pbtc", valueField: "placa", labelField: "placa" }, hint: "Placa da primeira carreta" }),
      f("dolly", "Dolly", "plate", { ref: { entity: "pbtc", valueField: "placa", labelField: "placa" }, hint: "Placa do dolly, se houver" }),
      f("segunda_carreta", "2ª carreta", "plate", { ref: { entity: "pbtc", valueField: "placa", labelField: "placa" }, hint: "Placa da segunda carreta, se houver" }),
      f("cidade_entrega", "Cidade de entrega", "reference", { ref: { entity: "regioes", valueField: "cidade_polo", labelField: "cidade_polo" }, hint: "Define região e tarifa" }),
      f("cliente_entrega", "Cliente da entrega", "text"),
      f("status_carga", "Status (meta)", "enum", { options: STATUS_META }),
      f("carga", "Carga", "text"),
      f("frete_especial", "Frete especial", "enum", { options: FRETE_ESPECIAL_OPTIONS, hint: "+30% na tarifa" }),
      f("data_hora_coleta", "Data/hora coleta (confirmada)", "datetime", { hint: "Só a programação (Torre) confirma ou altera o horário de coleta" }),
      f("urgente", "Urgente", "boolean"),
      f("prioridade", "Prioridade", "boolean"),
      f("critica", "Crítica", "boolean"),
      f("hora_agendamento", "Hora agendada", "text", { hint: "Horário combinado (alterações diárias)" }),
      // Editáveis desde 30/10/2026: as ordens da USIMINAS são lançadas à mão e
      // não existem na programação (DATA). Quando a ordem existe lá, o valor
      // de DATA prevalece (enrichViagemVega); senão vale o digitado.
      f("tipo_veiculo", "Tipo de veículo", "text", { hint: "Ex.: UK, U5, UC, UI, RN. Vem da programação (DATA) quando a ordem existe lá" }),
      f("peso", "Peso (t)", "number", { hint: "Vem da programação (DATA) quando a ordem existe lá" }),
      f("patio_coleta", "Pátio de coleta", "text"),
      f("unidade_origem", "Unidade de origem", "text", { hint: "Usina/unidade que despacha a carga (USIMINAS: Cubatão, Ipatinga…)" }),
      f("observacao", "Observação da carga", "textarea"),
      f("observacao_agendamento", "Observação do agendamento", "textarea", { hint: "Troca de motorista/ordem/horário no dia" }),
      // Derivados (calc engine) — read-only, com prévia no form
      // Pedido do gestor (POST /operacoes/:id/solicitar-horario). Read-only no
      // form da torre: ela responde confirmando em "Data/hora coleta".
      f("data_hora_solicitada", "Horário solicitado", "datetime", { computed: true }),
      f("data_criacao", "Data de criação", "date", { computed: true }),
      f("curso_autor", "Curso", "text", { computed: true }),
      f("exame_periodico", "Exame", "text", { computed: true }),
      f("atraso_coleta", "Atraso de coleta", "text", { computed: true }),
      f("pbtc_flag", "PBTC", "text", { computed: true }),
      f("regiao_meta", "Região da meta", "text", { computed: true }),
      f("tarifa", "Tarifa", "currency", { computed: true }),
      f("valor_total_frete", "Valor do frete", "currency", { computed: true }),
    ],
  },
];

const BY_ENTITY = new Map(ENTITIES.map((e) => [e.entity, e]));
export const getEntity = (name: string): EntitySpec | undefined => BY_ENTITY.get(name);
export const entityNames = (): string[] => ENTITIES.map((e) => e.entity);

// CPF: 11 dígitos (aceita com máscara). Placa: 7 alfanuméricos (antiga ou Mercosul).
const cpfRe = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;
const plateRe = /^[A-Za-z]{3}[-]?\d[A-Za-z0-9]\d{2}$/;

function fieldZod(field: FieldSpec): z.ZodTypeAny {
  switch (field.type) {
    case "number":
    case "currency":
      return z.coerce.number();
    case "boolean":
      // O form manda "true"/"false" (texto do checkbox). z.coerce.boolean()
      // leria "false" como true — toda carga desmarcada viraria marcada.
      return z.preprocess((v) => (v === "true" ? true : v === "false" ? false : v), z.boolean());
    case "enum":
      return z.enum([...(field.options ?? [""])] as [string, ...string[]]);
    case "cpf":
      return z.string().regex(cpfRe, "CPF inválido");
    case "plate":
      return z.string().regex(plateRe, "Placa inválida");
    case "date":
    case "datetime":
      return z.string().min(1);
    case "checklist":
      // Chega como texto JSON do form; validamos que é JSON de objeto.
      return z.string().refine((s) => {
        try { const v = JSON.parse(s); return v !== null && typeof v === "object"; }
        catch { return false; }
      }, "Checklist inválido");
    case "json":
      return z.any();
    default:
      return z.string();
  }
}

/**
 * Gera o schema Zod de uma entidade para validação de input.
 * - mode "create": campos required obrigatórios.
 * - mode "update": tudo opcional (PATCH parcial).
 * Campos `computed` são ignorados (calculados no servidor).
 */
export function buildZodSchema(spec: EntitySpec, mode: "create" | "update" = "create"): z.ZodObject<z.ZodRawShape> {
  const shape: z.ZodRawShape = {};
  for (const field of spec.fields) {
    if (field.computed) continue;
    let zt = fieldZod(field);
    const required = mode === "create" && field.required;
    if (!required) zt = zt.optional().nullable();
    shape[field.name] = zt;
  }
  return z.object(shape).strict();
}
